import { locatedAnchor } from '../../../shared/routeAnchor';
import { locatedStop } from '../../../shared/routeRetention';
import type { DetectedPos, PosSystem } from '../../../shared/enums';
import type { IsoDate, Lead, Visit } from '../../../shared/types';
import type { RouteStop, StoredRoute } from '../data/corridor';
import { formatTime } from './dates';
import { orderStops } from './route';
import { buildToday } from './today';

/** Pure planning for "Route today's follow-ups": the Today list turned into a day route (D27). */

/** Where a lead is, as found by data/geocode.ts. */
export interface LeadLocation {
  lat: number;
  lng: number;
  place_id?: string;
  address: string | null;
}

export interface FollowUpTarget {
  lead: Lead;
  daysOverdue: number;
  /** What to do there, e.g. "2 days late · Call to review proposal" or "10:30 AM · Demo KDS". */
  note: string;
}

/** Today's follow-ups, in the Today screen's order: overdue first, then due today (D9). */
export const followUpTargets = (
  leads: readonly Lead[],
  visits: readonly Visit[],
  today: IsoDate,
): FollowUpTarget[] => {
  const { overdue, dueToday } = buildToday(leads, visits, today);
  return [...overdue, ...dueToday].map(({ lead, visit, daysOverdue }) => {
    const when =
      daysOverdue > 0
        ? `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} late`
        : visit.next_action_time
          ? formatTime(visit.next_action_time)
          : 'today';
    return { lead, daysOverdue, note: [when, visit.next_action].filter(Boolean).join(' · ') };
  });
};

/** A lead's recorded POS in the route's detected-POS terms; systems we don't detect are "other". */
const DETECTED_FOR_POS: Record<PosSystem, DetectedPos> = {
  toast: 'toast',
  clover: 'clover',
  spoton: 'spoton',
  touchbistro: 'touchbistro',
  lightspeed: 'lightspeed',
  shopify: 'shopify',
  chownow: 'chownow',
  square: 'square',
  unknown: 'unknown',
  aloha: 'other',
  micros: 'other',
  cash_only: 'other',
  other: 'other',
};

export interface FollowUpPlan {
  route: StoredRoute;
  /** Follow-ups that could not be placed (no address, or the lookup found nothing). */
  unlocated: Lead[];
}

/** The day route through every located follow-up, nearest-first from `anchor` (D26). */
export const planFollowUpRoute = (
  targets: readonly FollowUpTarget[],
  located: ReadonlyMap<string, LeadLocation>,
  anchor: StoredRoute['anchor'],
  createdAt: string,
): FollowUpPlan => {
  const stops: RouteStop[] = [];
  const unlocated: Lead[] = [];
  for (const { lead, note } of targets) {
    const at = located.get(lead.id);
    if (!at) {
      unlocated.push(lead);
      continue;
    }
    stops.push({
      place_id: `lead:${lead.id}`,
      source: 'lead',
      name: lead.business_name,
      address: at.address ?? lead.address,
      lat: at.lat,
      lng: at.lng,
      types: [],
      detected_pos: DETECTED_FOR_POS[lead.current_pos],
      lead_id: lead.id,
      note,
    });
  }
  const start = locatedAnchor({ anchor, stops });
  const ordered = start ? orderStops(start, stops.filter(locatedStop)).map((o) => o.stop) : stops;
  return { route: { anchor, stops: ordered, built: true, created_at: createdAt }, unlocated };
};
